// src/adminDashboard/shared/components/PageShell.tsx
// Standard page wrapper component

import { ChevronRight } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

interface Breadcrumb {
    label: string;
    href?: string;
}

interface PageShellProps {
    title: string;
    subtitle?: string;
    breadcrumbs?: Breadcrumb[];
    actions?: React.ReactNode;
    children: React.ReactNode;
    isLoading?: boolean;
    className?: string;
}

const PageShell: React.FC<PageShellProps> = ({
    title,
    subtitle,
    breadcrumbs = [],
    actions,
    children,
    isLoading = false,
    className = '',
}) => {
    return (
        <div className={`space-y-6 ${className}`}>
            {/* Header section */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                    {/* Breadcrumbs */}
                    {breadcrumbs.length > 0 && (
                        <nav className="flex items-center gap-1 text-sm text-slate-500">
                            {breadcrumbs.map((crumb, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && <ChevronRight size={14} className="text-slate-400" />}
                                    {crumb.href ? (
                                        <Link
                                            to={crumb.href}
                                            className="hover:text-primary transition-colors"
                                        >
                                            {crumb.label}
                                        </Link>
                                    ) : (
                                        <span className="text-slate-900 font-medium">{crumb.label}</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </nav>
                    )}

                    {/* Title */}
                    <h1 className="text-2xl font-bold text-slate-900">{title}</h1>

                    {/* Subtitle */}
                    {subtitle && (
                        <p className="text-sm text-slate-500">{subtitle}</p>
                    )}
                </div>

                {/* Actions */}
                {actions && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {actions}
                    </div>
                )}
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <p className="text-sm text-slate-500">Loading...</p>
                    </div>
                </div>
            ) : (
                children
            )}
        </div>
    );
};

export default PageShell;
